// Lettings → Viewings (/app/lettings/viewings; ?sub=viewings redirects here) — viewing requests and viewings in one server-paginated table, 20 rows a
// page, on GET /lettings/viewings (docs/plans/lettings-one-form.md, phase 2). One Filter: When (presets, default Today,
// with an hour window — the preset is remembered on this browser, lotus.viewings.when), Property and Status (with the
// counts the board returns). Rows carry no buttons (26 Oct 2026, later): a row click opens the ViewingDrawer, whose footer
// has the next steps (request: Book · Reject; booked: Assign host · Reschedule · Mark completed · Cancel viewing; completed:
// Register for home · Mark lost — no Convert since 3 Nov 2026).
// Header: New viewing request (the website's four-step form → POST /leads, or contact details only) and the CSV exports.
// Older links keep working: ?lead=<id> opens that request, ?feedback=<viewing id> opens that viewing, ?show= and
// ?view=requests / booked pick the status, ?search= searches — and any of them widens When to Any time so the row shows.
// ?when=<preset> (the page's tiles) sets When for that visit without remembering it.
// Since 3 Nov 2026 (docs/plans/lettings-ui-simplification.md, phase 2) the Filter has a second date group, **Booked for**
// (the viewing's time, or what a request asked for — GET /lettings/viewings atFrom / atTo; default Any time, not
// remembered; ?for=<preset>), and the When group is labelled Booked at, as on Applications. The tiles sit on this page
// (pages/Lettings.tsx) and set ?show=, ?when= and ?for= while it is open; the tab follows them, and with them gone (the
// tile clicked again) it is back to the default (the remembered When, every status, Booked for Any time). The Filter's
// Apply drops them, so a tile stays lit only while its filter is the one on screen.
// Since 29 Oct 2026 (later) When is over Booked at — when the request or viewing was submitted — for every row, and the
// list is newest submitted first (viewingsBoard.service.ts). Booked at ("Thu 29 Oct, 10:14") then Booked for: the
// viewing's time, or what a request asked for ("Asked for Fri 30 Oct, 10:00", "No time given"); both sort on the server.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../stores/auth';
import { useList } from '../../hooks/useList';
import { DataTable, type Column } from '../ui/DataTable';
import { PageHeader, Button, Field, Select, Sheet } from '../ui';
import { FilterMenu, type FilterValue } from '../ui/FilterMenu';
import { FormError, toMajor } from '../forms/common';
import { usePropertyOptions, useListingOptions } from '../../lib/options';
import { leadsApi, leadSourceLabel, viewingChip, type BoardStatus, type ViewingsBoardExtra, type ViewingsBoardRow } from '../../lib/lettings';
import { emptyApplication, nextWeekday, serverFieldError, slotErrors, toPayload, withFreeFrom, type ApplicationState } from '../../lib/applicationForm';
import { contactOnlyErrors, contactOnlyPayload, formDocsPayload, moneyBlock, siteFormErrors, useFormSteps, type FormDocs } from '../../lib/applicationSteps';
import { fmtDay, fmtWhen, initialWhen, isDatePreset, rememberWhen, whenQuery } from '../../lib/dates';
import { Chip } from './bits';
import { ApplicationSteps, SlotsField, StepButtons } from './ApplicationFields';
import { ViewingDrawer, type ViewingRef } from './ViewingDrawer';
import { formatPhone } from '../../lib/phone';

const WHEN_KEY = 'lotus.viewings.when';
const STATUSES: Array<{ key: BoardStatus; label: string }> = [
  { key: 'requests', label: 'New requests' }, { key: 'booked', label: 'Booked' }, { key: 'completed', label: 'Completed' }, { key: 'other', label: 'The rest' },
];
export function ViewingsTab() {
  const { canAccessPage, can } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const allowed = canAccessPage('leads') || canAccessPage('viewings');

  // Older links: they name a record or a status, so When opens on Any time (and is not remembered).
  const legacy = params.get('view');
  const linkedStatus = (params.get('show') as BoardStatus | 'all' | null) ?? (legacy === 'requests' ? 'requests' : legacy === 'booked' ? 'booked' : null);
  const linked = !!(linkedStatus || params.get('lead') || params.get('feedback') || params.get('search'));
  // ?when=<preset> (the counts row's tiles) sets When for this visit only.
  const forOf = (p: URLSearchParams) => { const f = p.get('for'); return [f && isDatePreset(f) ? f : 'any']; };
  const [fv, setFv] = useState<FilterValue>(() => ({
    when: initialWhen(WHEN_KEY, 'today', { url: params.get('when'), linked }),
    at: forOf(params),
    status: linkedStatus && linkedStatus !== 'all' ? [linkedStatus] : [],
  }));
  // Booked for: sent only when it is not Any time.
  const atOf = (v: FilterValue) => { if ((v.at?.[0] ?? 'any') === 'any') return {}; const w = whenQuery(v.at, 'any'); return { atFrom: w.from, atTo: w.to }; };
  const filtersOf = (v: FilterValue) => ({ ...whenQuery(v.when), ...atOf(v), propertyId: v.property?.[0] || undefined, status: v.status?.[0] || undefined });
  const list = useList<ViewingsBoardRow>('/lettings/viewings', filtersOf(fv), { perPage: 20, enabled: allowed });
  // A tile setting (or clearing) ?show= / ?when= / ?for= while the tab is open: follow it. The Filter's Apply drops them,
  // and that must not undo what was just picked, so it moves `seenLink` along with the URL.
  const linkKey = `${params.get('show') ?? ''}|${params.get('when') ?? ''}|${params.get('for') ?? ''}`;
  const [seenLink, setSeenLink] = useState(linkKey);
  if (seenLink !== linkKey) {
    setSeenLink(linkKey);
    const show = params.get('show') as BoardStatus | 'all' | null;
    const v = { ...fv, when: initialWhen(WHEN_KEY, 'today', { url: params.get('when'), linked: !!show }), at: forOf(params), status: show && show !== 'all' ? [show] : [] };
    setFv(v); list.setFilters(filtersOf(v));
  }
  const applyFilter = (v: FilterValue) => {
    setFv(v); list.setFilters(filtersOf(v));
    rememberWhen(WHEN_KEY, v.when, 'today');
    if (params.has('show') || params.has('when') || params.has('for') || params.has('view')) {
      const n = new URLSearchParams(params); n.delete('show'); n.delete('when'); n.delete('for'); n.delete('view');
      setSeenLink('||'); setParams(n, { replace: true });
    }
  };
  const extra = list.extra as ViewingsBoardExtra | undefined;
  const properties = usePropertyOptions();

  const [drawer, setDrawer] = useState<ViewingRef | null>(() => {
    const lead = params.get('lead'), fb = params.get('feedback');
    if (fb) return { kind: 'viewing', viewingId: fb };
    if (lead) return { kind: 'request', leadId: lead };
    return null;
  });
  const [creating, setCreating] = useState(false);
  const open = (r: ViewingsBoardRow) => setDrawer({ kind: r.kind, leadId: r.leadId, viewingId: r.viewingId });
  const close = () => {
    setDrawer(null);
    void qc.invalidateQueries({ queryKey: ['/lettings/viewings'] });
    if (params.has('lead') || params.has('feedback')) { const n = new URLSearchParams(params); n.delete('lead'); n.delete('feedback'); setParams(n, { replace: true }); }
  };

  const preset = fv.when?.[0] || 'today';
  const columns = useMemo<Column<ViewingsBoardRow>[]>(() => [
    { key: 'who', label: 'Who', sortable: false, render: (r) => (
      <div className="max-w-[220px]">
        <div className="truncate font-medium text-ink-900">{r.name ?? r.email ?? (formatPhone(r.phone) || '—')}</div>
        <div className="truncate text-[11.5px] text-ink-500">{[leadSourceLabel(r.source), r.email ?? formatPhone(r.phone)].filter(Boolean).join(' · ')}</div>
      </div>
    ) },
    { key: 'home', label: 'Home', sortable: false, hideOnMobile: true, render: (r) => (
      <div className="max-w-[200px]"><div className="truncate">{r.listingHeadline ?? r.propertyName ?? '—'}</div>{r.listingHeadline && r.propertyName && <div className="truncate text-[11.5px] text-ink-500">{r.propertyName}</div>}</div>
    ) },
    { key: 'submittedAt', label: 'Booked at', sortKey: 'submittedAt', render: (r) => <span className="whitespace-nowrap text-ink-700">{fmtWhen(r.submittedAt)}</span> },
    { key: 'at', label: 'Booked for', sortKey: 'at', render: (r) => r.kind === 'viewing'
      ? <div><div className="whitespace-nowrap font-medium text-ink-900">{fmtWhen(r.at)}</div>{r.agent && <div className="text-[11.5px] text-ink-500">with {r.agent.name}</div>}</div>
      : <AskedFor r={r} /> },
    { key: 'status', label: 'Status', sortable: false, render: (r) => { const c = viewingChip(r.kind, r.status, r.outcome, r.kind === 'viewing' ? r.at : null); return <Chip label={c.label} colour={c.colour} />; } },
  ], []);

  if (!allowed) return null;
  const counts = extra?.counts;
  return (
    <div>
      <PageHeader eyebrow="Lettings" title="Viewings" menu={[
        { label: 'New viewing request', icon: 'Plus', can: 'leads.create', onClick: () => setCreating(true) },
        can('leads.export') && { label: 'Export leads (CSV)', export: { path: '/leads', page: 'leads', query: {} } },
        can('viewings.export') && { label: 'Export viewings (CSV)', export: { path: '/viewings', page: 'viewings', query: {} } },
      ]} />
      <DataTable columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage} isLoading={list.isLoading} isFetching={list.isFetching}
        rowKey={(r) => `${r.kind}:${r.id}`} onRowClick={(r) => open(r)} sort={list.sort} onSort={(v) => { list.setPage(1); list.setSort(v); }}
        search={list.search} onSearch={list.setSearch} searchPlaceholder="Search name, email, phone or home…"
        filters={<FilterMenu value={fv} onChange={applyFilter} groups={[
          { key: 'when', label: 'Booked at', options: [], input: 'dateRange', hours: true, defaultValue: 'today' },
          { key: 'at', label: 'Booked for', options: [], input: 'dateRange', defaultValue: 'any' },
          { key: 'property', label: 'Property', single: true, options: (properties.data ?? []).map((p) => ({ value: String(p.id), label: String(p.name) })) },
          { key: 'status', label: 'Status', single: true, options: STATUSES.map((x) => ({ value: x.key, label: counts ? `${x.label} (${counts[x.key]})` : x.label })) },
        ]} />}
        emptyTitle={preset === 'today' && !fv.status?.[0] && (fv.at?.[0] ?? 'any') === 'any' && !list.search ? 'Nothing came in today' : 'Nothing here'}
        emptyBody={preset === 'today' ? 'No viewing requests or viewings were booked today — change Booked at to see other days.' : 'Try another Booked at, Booked for, Property or Status.'} />

      {drawer && <ViewingDrawer key={`${drawer.leadId}:${drawer.viewingId}`} target={drawer} onClose={close} />}
      {creating && <NewRequestSheet onClose={(made) => { setCreating(false); if (made) { void qc.invalidateQueries({ queryKey: ['/lettings/viewings'] }); setDrawer({ kind: 'request', leadId: made }); } }} />}
    </div>
  );
}

/** A request's Booked for: the time they asked for (first of their slots) — a wish, not a booking. */
function AskedFor({ r }: { r: ViewingsBoardRow }) {
  const first = r.slots[0];
  if (!first) return <span className="whitespace-nowrap text-[12px] italic text-ink-500">No time given</span>;
  const more = r.slots.length - 1;
  return (
    <div className="text-[12px] italic text-ink-500">
      <div className="whitespace-nowrap">Asked for {fmtDay(first.date)}, {first.time ?? first.part ?? 'any time'}</div>
      {more > 0 && <div className="whitespace-nowrap text-ink-400">or {more} more time{more > 1 ? 's' : ''}</div>}
    </div>
  );
}

/**
 * Staff "New viewing request": the home, how it came in and when suits them, then the website's form in its four steps
 * (ApplicationSteps — the same rules, the passport and right to rent document on step 2, the three consents) → POST /leads
 * (it lands as a New request). Someone on the phone: "Save with contact details only" on step 1 saves the name, email and
 * mobile with the time (contactOnly) — the rest is asked for before it can become an application.
 */
function NewRequestSheet({ onClose }: { onClose: (leadId: string | null) => void }) {
  const listings = useListingOptions();
  const [listingId, setListingId] = useState('');
  const [source, setSource] = useState('PHONE');
  const [s, setS] = useState<ApplicationState>(() => emptyApplication({ preferredSlots: [{ date: nextWeekday(1), time: '10:00' }] }));
  const [docs, setDocs] = useState<FormDocs>({});
  const [tried, setTried] = useState(false);
  const listing = (listings.data ?? []).find((x) => x.id === listingId);
  // A home picked: the move-in starts at the day that flat is free (today when it is free now) and cannot go earlier, and
  // the rent offered starts at its advert (3 Nov 2026) — unless someone typed their own figure for the home before.
  const pickListing = (id: string) => {
    const was = toMajor(Number(listing?.advertisedRentMinor ?? 0) || null);
    setListingId(id);
    const l = (listings.data ?? []).find((x) => x.id === id);
    const rent = toMajor(Number(l?.advertisedRentMinor ?? 0) || null);
    setS((cur) => ({ ...withFreeFrom(cur, l?.freeFrom ?? null, { notice: !!l?.underNotice, reset: true }), ...(!cur.offeredRent.trim() || cur.offeredRent === was ? { offeredRent: rent } : {}) }));
  };
  const advert = Number(listing?.advertisedRentMinor ?? 0) || 0;
  const slotErr = slotErrors(s).preferredSlots;
  const save = useMutation({
    mutationFn: (contactOnly: boolean) => leadsApi.create(contactOnly
      ? { listingId: listingId || null, source, contactOnly: true, form: contactOnlyPayload(s) }
      : { listingId: listingId || null, source, form: toPayload(s, { mode: 'viewing', complete: true }), documents: formDocsPayload(s.nationality, docs) }),
    onSuccess: (lead) => onClose(lead.id),
  });
  // The home and the time sit above the steps: both are checked before either way of saving.
  const ready = () => { setTried(true); return !!listingId && !slotErr; };
  const steps = useFormSteps(siteFormErrors(s, advert, { mode: 'viewing', docs, slots: false }), { error: save.error, onSubmit: () => { if (ready()) save.mutate(false); }, blocks: moneyBlock(s, advert) });
  const contactOnly = () => {
    const bad = Object.keys(contactOnlyErrors(s));
    if (bad.length) { setTried(true); steps.touchFields(bad); return; }
    if (ready()) save.mutate(true);
  };
  return (
    <Sheet open onClose={() => onClose(null)} size="lg" eyebrow="Lettings · Viewings" title="New viewing request" subtitle="Someone who phoned or walked in — it lands as a New request to book."
      footer={<>
        <Button variant="secondary" className="mr-auto" onClick={() => onClose(null)}>Cancel</Button>
        <StepButtons steps={steps} submitLabel="Save request" pending={save.isPending}
          before={steps.step === 0 && <Button variant="secondary" onClick={contactOnly} disabled={save.isPending}>Save with contact details only (fill the rest later)</Button>} />
      </>}>
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
          <div>
            <Field label="Home *"><Select value={listingId} onChange={(e) => pickListing(e.target.value)} className="w-full"><option value="">Choose a listing…</option>{(listings.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.headline}</option>)}</Select></Field>
            {tried && !listingId && <span className="mt-1 block text-[11px] text-red-600">Pick the home they want to see</span>}
          </div>
          <Field label="Came in by"><Select value={source} onChange={(e) => setSource(e.target.value)} className="w-full"><option value="PHONE">Phone</option><option value="WALK_IN">Walk-in</option><option value="REFERRAL">Referral</option><option value="PORTAL">Other</option></Select></Field>
        </div>
        <SlotsField s={s} set={(p) => setS((cur) => ({ ...cur, ...p }))} error={tried ? slotErr : undefined} />
        <div className="border-t border-ink-100 pt-4">
          <ApplicationSteps steps={steps} s={s} set={(p) => setS((cur) => ({ ...cur, ...p }))} advert={advert} docs={docs} setDocs={setDocs} />
        </div>
        {!serverFieldError(save.error) && <FormError error={save.error} />}
      </div>
    </Sheet>
  );
}
